import { QueryClient } from '@tanstack/react-query';

const CHANNEL_NAME = 'moneyskyline:timezone-cache-invalidation';
const STORAGE_KEY = 'moneyskyline:timezone-cache-invalidation';
const MAX_SEEN_EVENTS = 100;

type QueryWithKey = { queryKey: readonly unknown[] };
type CacheSignal = { kind: 'timezone-cache-invalidated'; eventId: string };
type ProfileTimezone = { timezone: string };

type ListenerState = {
  count: number;
  channel: BroadcastChannel | null;
  onStorage: (event: StorageEvent) => void;
  onMismatch: (event: Event) => void;
  seenEvents: Set<string>;
  seenMismatchPairs: Set<string>;
  release: () => void;
};

const listenerStates = new WeakMap<QueryClient, ListenerState>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function hasTimezone(value: unknown): value is { timezone: unknown } {
  return isRecord(value) && 'timezone' in value;
}

export function isDateDependentQuery(query: QueryWithKey, timezone: string): boolean {
  if (query.queryKey[0] === 'business-year') return true;
  if (query.queryKey[0] !== 'transactions') return false;
  const filters = query.queryKey[1];
  return hasTimezone(filters) && filters.timezone === timezone;
}

export async function retireTimezone(queryClient: QueryClient, timezone: string): Promise<void> {
  const predicate = (query: QueryWithKey) => isDateDependentQuery(query, timezone);
  await queryClient.cancelQueries({ predicate });
  queryClient.removeQueries({ predicate });
}

export async function invalidateActiveTimezone(queryClient: QueryClient, timezone: string): Promise<void> {
  await queryClient.invalidateQueries({
    predicate: (query) => isDateDependentQuery(query, timezone),
    refetchType: 'active',
  });
}

function remember(set: Set<string>, value: string): boolean {
  if (set.has(value)) return false;
  set.add(value);
  if (set.size > MAX_SEEN_EVENTS) {
    const oldest = set.values().next().value;
    if (oldest !== undefined) set.delete(oldest);
  }
  return true;
}

function nextEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isCacheSignal(value: unknown): value is CacheSignal {
  return isRecord(value) && value.kind === 'timezone-cache-invalidated' && typeof value.eventId === 'string' && value.eventId.length > 0;
}

function onRemoteInvalidation(queryClient: QueryClient, state: ListenerState, value: unknown): void {
  if (!isCacheSignal(value) || !remember(state.seenEvents, value.eventId)) return;
  const confirmed = queryClient.getQueryData<ProfileTimezone>(['me'])?.timezone;
  if (confirmed) void retireTimezone(queryClient, confirmed);
  void queryClient.invalidateQueries({ queryKey: ['me'], exact: true, refetchType: 'active' });
}

export function publishTimezoneInvalidation(): void {
  if (typeof window === 'undefined') return;
  const signal: CacheSignal = { kind: 'timezone-cache-invalidated', eventId: nextEventId() };
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(signal);
      channel.close();
      return;
    }
  } catch {
    // Fall through to the storage pulse when BroadcastChannel is unavailable.
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, signal.eventId);
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Cache invalidation is best effort when browser storage is unavailable.
  }
}

export function registerTimezoneCacheListeners(queryClient: QueryClient): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const existing = listenerStates.get(queryClient);
  if (existing) {
    existing.count += 1;
    return existing.release;
  }

  const state: ListenerState = {
    count: 1,
    channel: null,
    seenEvents: new Set(),
    seenMismatchPairs: new Set(),
    onStorage: () => undefined,
    onMismatch: () => undefined,
    release: () => undefined,
  };
  const onSignal = (value: unknown) => onRemoteInvalidation(queryClient, state, value);
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      state.channel = new BroadcastChannel(CHANNEL_NAME);
      state.channel.onmessage = (event) => onSignal(event.data);
    } catch {
      state.channel = null;
    }
  }
  state.onStorage = (event) => {
    if (event.key === STORAGE_KEY && event.newValue) onSignal({ kind: 'timezone-cache-invalidated', eventId: event.newValue });
  };
  state.onMismatch = (event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (!isRecord(detail) || typeof detail.confirmedTimezone !== 'string' || typeof detail.responseTimezone !== 'string') return;
    const pair = `${detail.confirmedTimezone}\u0000${detail.responseTimezone}`;
    if (!remember(state.seenMismatchPairs, pair)) return;
    void queryClient.invalidateQueries({ queryKey: ['me'], exact: true, refetchType: 'active' });
  };
  window.addEventListener('storage', state.onStorage);
  window.addEventListener('moneyskyline:temporal-scope-mismatch', state.onMismatch);
  state.release = () => {
    state.count -= 1;
    if (state.count > 0) return;
    state.channel?.close();
    window.removeEventListener('storage', state.onStorage);
    window.removeEventListener('moneyskyline:temporal-scope-mismatch', state.onMismatch);
    listenerStates.delete(queryClient);
  };
  listenerStates.set(queryClient, state);
  return state.release;
}

export async function commitTimezoneChange(queryClient: QueryClient, oldTimezone: string | undefined, newTimezone: string): Promise<void> {
  if (oldTimezone === newTimezone) return;
  if (oldTimezone) await retireTimezone(queryClient, oldTimezone);
  await invalidateActiveTimezone(queryClient, newTimezone);
  publishTimezoneInvalidation();
}
